import type { DayId } from './days'
import { normalizeIngredients } from './normalizeIngredients'
import type { MealBlock, TrainingBlock, TrainingExercise, UserProtocol } from './protocolTypes'
import { normalizeCoolDownProtocol, type CoolDownProtocol } from './coolDownProtocol'
import { supabase } from './supabaseClient'

export async function loadUserProtocol(userId: string): Promise<{ protocol: UserProtocol | null; error: string | null }> {
  const { data: schedules, error: scheduleError } = await supabase
    .from('training_schedules')
    .select('*')
    .eq('user_id', userId)
    .order('scheduled_date', { ascending: true })

  if (scheduleError) {
    return { protocol: null, error: scheduleError.message }
  }

  const scheduleIds = (schedules ?? []).map((s) => s.id)
  const exercisesBySchedule = new Map<string, TrainingExercise[]>()

  if (scheduleIds.length > 0) {
    const { data: exerciseRows, error: exerciseError } = await supabase
      .from('training_block_exercises')
      .select('*')
      .in('schedule_id', scheduleIds)
      .order('display_order', { ascending: true })

    if (exerciseError) {
      return { protocol: null, error: exerciseError.message }
    }

    for (const row of exerciseRows ?? []) {
      const list = exercisesBySchedule.get(row.schedule_id) ?? []
      list.push({
        id: row.id,
        exercise_name: row.exercise_name ?? '',
        prescription_text: row.prescription_text ?? '',
        form_instructions: row.form_instructions ?? '',
        warm_up_instructions: row.warm_up_instructions,
        cool_down_instructions: row.cool_down_instructions,
        display_order: row.display_order ?? 0,
        is_completed: row.is_completed ?? false,
      })
      exercisesBySchedule.set(row.schedule_id, list)
    }
  }

  // Deduplicate by day_name_id — keep the row with the latest scheduled_date.
  // Guards against any stale duplicate rows that predate the DB UNIQUE constraint.
  const scheduleDeduped = [...(schedules ?? [])
    .reduce((map, s) => {
      const existing = map.get(s.day_name_id)
      if (!existing || (s.scheduled_date ?? '') > (existing.scheduled_date ?? '')) {
        map.set(s.day_name_id, s)
      }
      return map
    }, new Map<string, typeof schedules[number]>())
    .values()]

  const training: TrainingBlock[] = scheduleDeduped.map((s) => ({
    id: s.id,
    day_name_id: s.day_name_id as DayId,
    scheduled_date: s.scheduled_date,
    window_type: s.window_type ?? '',
    start_time: String(s.start_time ?? '').slice(0, 5),
    end_time: String(s.end_time ?? '').slice(0, 5),
    block_title: s.block_title ?? '',
    focus_subtitle: s.focus_subtitle ?? '',
    coach_why_rationale: s.coach_why_rationale ?? '',
    cool_down_protocol: normalizeCoolDownProtocol(
      s.cool_down_protocol as CoolDownProtocol | null,
      exercisesBySchedule.get(s.id)?.map((ex) => ({ exercise_name: ex.exercise_name })) ?? [],
    ),
    exercises: exercisesBySchedule.get(s.id) ?? [],
  }))

  const { data: meals, error: mealError } = await supabase
    .from('meal_protocols')
    .select('*')
    .eq('user_id', userId)
    .order('time_stamp', { ascending: true })

  if (mealError) {
    return { protocol: null, error: mealError.message }
  }

  const { data: grocery, error: groceryError } = await supabase
    .from('grocery_manifest')
    .select(
      'id, item_name, quantity_mass, macro_category, approx_cost, store_chain, store_distance_km, store_address, is_secured, amount_remaining, is_depleted, retailer_name, is_estimated, preferred_store_name, coaching_reason, coaching_reason_generated_at',
    )
    .eq('user_id', userId)
    .order('item_name', { ascending: true })

  if (groceryError) {
    return { protocol: null, error: groceryError.message }
  }

  return {
    protocol: {
      training,
      meals: (meals ?? []).map((m) => ({
        id: m.id,
        day_id: m.day_id as DayId,
        block_name: m.block_name ?? '',
        time_stamp: String(m.time_stamp ?? '').slice(0, 5),
        dish_name: m.dish_name ?? '',
        calories_kcal: m.calories_kcal ?? 0,
        protein_g: m.protein_g ?? 0,
        carbs_g: m.carbs_g ?? 0,
        fat_g: m.fat_g ?? 0,
        rationale_why: m.rationale_why ?? '',
        ingredients: normalizeIngredients(m.ingredients),
        cooking_instructions: Array.isArray(m.cooking_instructions)
          ? (m.cooking_instructions as MealBlock['cooking_instructions'])
          : [],
        visual_description: m.visual_description ?? undefined,
        cooking_time_minutes: m.cooking_time_minutes ?? undefined,
        difficulty: m.difficulty ?? undefined,
        storage_protocol: m.storage_protocol as MealBlock['storage_protocol'],
        image_url: m.image_url ?? undefined,
      })),
      grocery: (grocery ?? []).map((g) => ({
        id: g.id,
        item_name: g.item_name,
        quantity_mass: g.quantity_mass ?? '',
        macro_category: g.macro_category ?? '',
        approx_cost: Number(g.approx_cost ?? 0),
        store_chain: g.store_chain ?? '',
        store_distance_km: g.store_distance_km != null ? Number(g.store_distance_km) : null,
        store_address: g.store_address ?? null,
        retailer_name: g.retailer_name ?? null,
        is_estimated: g.is_estimated ?? true,
        preferred_store_name: g.preferred_store_name ?? null,
        is_secured: g.is_secured ?? false,
        amount_remaining: g.amount_remaining != null ? Number(g.amount_remaining) : null,
        is_depleted: g.is_depleted ?? false,
        coaching_reason: g.coaching_reason ?? null,
        coaching_reason_generated_at: g.coaching_reason_generated_at ?? null,
      })),
    },
    error: null,
  }
}

export function estimateBlockMinutes(block: TrainingBlock): number {
  const start = block.start_time.split(':').map(Number)
  const end = block.end_time.split(':').map(Number)
  if (start.length === 2 && end.length === 2) {
    const mins = end[0] * 60 + end[1] - (start[0] * 60 + start[1])
    if (mins > 0) return mins
  }
  return block.exercises.length * 12 + 15
}
